import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { useSupabase, useSupabaseSubscription } from '../hooks/useSupabase';
import Header from '../components/Header';

interface Contribution {
  id: string;
  contributor_name: string;
  contributor_relationship: string | null;
  contribution_type: string;
  content: {
    favourite_memory?: string;
    personality_traits?: string;
    additional_notes?: string;
  };
  is_approved: boolean;
  created_at: string;
}

interface Collaborator {
  id: string;
  email: string;
  full_name: string | null;
  status: string;
  invited_at: string;
}

interface ActivityItem {
  id: string;
  action_type: string;
  action_description: string;
  metadata: Record<string, any>;
  created_at: string;
}

interface SessionInfo {
  id: string;
  type: string;
  status: string;
  testament_licenses?: {
    deceased_person_name?: string;
  };
}

const CollaborationDashboard: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const { supabase, loading: supabaseLoading } = useSupabase();

  const [session, setSession] = useState<SessionInfo | null>(null);
  const [contributions, setContributions] = useState<Contribution[]>([]);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [presenceCount, setPresenceCount] = useState(0);
  const [presenceUsers, setPresenceUsers] = useState<string[]>([]);

  // Load initial data via serverless API (bypasses RLS)
  useEffect(() => {
    if (!isLoaded || !isSignedIn || !sessionId) return;

    async function loadData() {
      setLoading(true);
      setError(null);

      try {
        const token = await getToken();
        if (!token) throw new Error('Not authenticated');

        const resp = await fetch(`/api/collaboration/dashboard?sessionId=${encodeURIComponent(sessionId!)}`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });

        if (!resp.ok) {
          const errData = await resp.json().catch(() => ({}));
          throw new Error(errData.error || 'Failed to load collaboration data');
        }

        const data = await resp.json();
        setSession(data.session as SessionInfo);
        setContributions(data.contributions as Contribution[]);
        setCollaborators(data.collaborators as Collaborator[]);
        setActivity(data.activity as ActivityItem[]);
      } catch (err: any) {
        setError(err.message || 'Failed to load collaboration data');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [isLoaded, isSignedIn, sessionId, getToken]);

  // Real-time subscription for new contributions (works via Supabase Realtime, no RLS needed)
  const handleContributionChange = useCallback((payload: any) => {
    if (payload.eventType === 'INSERT') {
      setContributions(prev => [payload.new as Contribution, ...prev]);
    } else if (payload.eventType === 'UPDATE') {
      setContributions(prev =>
        prev.map(c => c.id === payload.new.id ? (payload.new as Contribution) : c)
      );
    } else if (payload.eventType === 'DELETE') {
      setContributions(prev => prev.filter(c => c.id !== payload.old.id));
    }
  }, []);

  useSupabaseSubscription(
    'eulogy_contributions',
    `session_id=eq.${sessionId}`,
    handleContributionChange
  );

  // Real-time subscription for activity log
  const handleActivityChange = useCallback((payload: any) => {
    if (payload.eventType === 'INSERT') {
      setActivity(prev => [payload.new as ActivityItem, ...prev].slice(0, 20));
    }
  }, []);

  useSupabaseSubscription(
    'activity_log',
    `session_id=eq.${sessionId}`,
    handleActivityChange
  );

  // Presence channel
  useEffect(() => {
    if (!supabase || !sessionId) return;

    const channel = supabase.channel(`presence-${sessionId}`, {
      config: { presence: { key: 'owner' } },
    });

    channel
      .on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        const allUsers: string[] = [];
        Object.values(state).forEach((presences: any) => {
          presences.forEach((p: any) => {
            if (p.name) allUsers.push(p.name);
          });
        });
        setPresenceCount(allUsers.length);
        setPresenceUsers(allUsers);
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await channel.track({ name: 'Owner', role: 'owner' });
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [supabase, sessionId]);

  // Toggle contribution approval via direct Supabase (service role not needed for updates from owner)
  async function toggleApproval(contributionId: string, currentlyApproved: boolean) {
    if (!supabase) return;

    // Use the API to toggle since RLS may block direct updates too
    try {
      const token = await getToken();
      const resp = await fetch(`/api/contributions/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ contributionId, approved: !currentlyApproved }),
      });

      if (resp.ok) {
        setContributions(prev =>
          prev.map(c => c.id === contributionId ? { ...c, is_approved: !currentlyApproved } : c)
        );
      }
    } catch (err) {
      console.error('Error toggling approval:', err);
      // Optimistic fallback — update locally anyway
      setContributions(prev =>
        prev.map(c => c.id === contributionId ? { ...c, is_approved: !currentlyApproved } : c)
      );
    }
  }

  // Format relative time
  function timeAgo(dateStr: string): string {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  }

  // Loading states
  if (!isLoaded || supabaseLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-background">
        <div className="text-center">
          <img src="/GhostQuillLogo.png" alt="Cyrano" className="w-16 h-16 mx-auto animate-pulse" />
          <p className="text-cool-gray mt-4">Loading collaboration dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    navigate('/sign-in');
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-space-cadet mx-auto"></div>
          <p className="text-cool-gray mt-4">Loading contributions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-warm-background px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
          <img src="/Assets/200-handdrawn-icons-by-heloholo/png/48/warning48.png" alt="" className="w-12 h-12 mx-auto mb-4" style={{ filter: 'brightness(0) saturate(100%) invert(15%) sepia(30%) saturate(2000%) hue-rotate(200deg) brightness(90%) contrast(95%)' }} />
          <h2 className="font-serif text-xl text-space-cadet mb-2">Something went wrong</h2>
          <p className="text-cool-gray mb-4">{error}</p>
          <button onClick={() => navigate('/dashboard')} className="text-space-cadet-primary hover:underline">
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  const deceasedName = (session?.testament_licenses as any)?.deceased_person_name || 'your loved one';
  const sessionType = session?.type || 'tribute';
  const approvedCount = contributions.filter(c => c.is_approved).length;

  return (
    <div className="min-h-screen bg-warm-background">
      {/* Shared app header — global nav + the same slide-in menu as the rest of the app */}
      <Header />
      {/* Page sub-header — context for this collaboration view (de-stickied so it
          doesn't fight the shared sticky header for the top of the screen) */}
      <header className="bg-white shadow-sm border-b border-cool-gray/10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/GhostQuillLogo.png" alt="Cyrano" className="w-8 h-8 md:w-10 md:h-10 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="font-serif text-lg md:text-xl text-space-cadet font-semibold truncate">
                Memories for {deceasedName}
              </h1>
              <p className="text-sm text-cool-gray">
                {contributions.length} contribution{contributions.length !== 1 ? 's' : ''} received
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Presence indicator */}
            {presenceCount > 1 && (
              <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1.5 rounded-full text-sm">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                {presenceCount - 1} viewing
              </div>
            )}
            <button
              onClick={() => {
                // Navigate back to the in-progress conversation (not the onboarding dashboard)
                const params = new URLSearchParams({
                  sessionId: sessionId || '',
                  type: sessionType,
                  name: deceasedName,
                });
                navigate(`/conversation?${params.toString()}`);
              }}
              className="text-sm text-cool-gray hover:text-space-cadet transition-colors"
            >
              Back to Conversation
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Main column — Contributions */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="font-serif text-lg text-space-cadet font-semibold">
                Shared Memories
              </h2>
              <span className="text-sm text-cool-gray">
                {approvedCount} approved
              </span>
            </div>

            {contributions.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                <img
                  src="/Assets/200-handdrawn-icons-by-heloholo/png/48/comment148.png"
                  alt=""
                  className="w-12 h-12 mx-auto mb-4"
                  style={{ filter: 'brightness(0) saturate(100%) invert(55%) sepia(10%) hue-rotate(200deg)' }}
                />
                <p className="text-cool-gray text-lg mb-2">No memories shared yet</p>
                <p className="text-cool-gray text-sm">
                  When collaborators submit their memories, they'll appear here in real-time.
                </p>
              </div>
            ) : (
              contributions.map((contrib) => (
                <div
                  key={contrib.id}
                  className={`bg-white rounded-xl shadow-sm p-5 border-l-4 transition-all ${
                    contrib.is_approved ? 'border-space-cadet' : 'border-cool-gray opacity-60'
                  }`}
                >
                  {/* Contributor info */}
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="font-semibold text-space-cadet">
                        {contrib.contributor_name}
                      </span>
                      {contrib.contributor_relationship && (
                        <span className="text-cool-gray text-sm ml-2">
                          — {contrib.contributor_relationship}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-cool-gray">{timeAgo(contrib.created_at)}</span>
                      <button
                        onClick={() => toggleApproval(contrib.id, contrib.is_approved)}
                        className={`text-sm px-3 py-2 min-h-[36px] rounded-full transition-colors ${
                          contrib.is_approved
                            ? 'bg-green-50 text-green-700 hover:bg-red-50 hover:text-red-600'
                            : 'bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-700'
                        }`}
                        title={contrib.is_approved ? 'Click to exclude from eulogy' : 'Click to include in eulogy'}
                      >
                        {contrib.is_approved ? 'Approved' : 'Excluded'}
                      </button>
                    </div>
                  </div>

                  {/* Memory content */}
                  {contrib.content.favourite_memory && (
                    <div className="mb-3">
                      <p className="text-xs text-cool-gray uppercase tracking-wide mb-1">Favourite Memory</p>
                      <p className="text-elegant-black leading-relaxed">
                        {contrib.content.favourite_memory}
                      </p>
                    </div>
                  )}

                  {contrib.content.personality_traits && (
                    <div className="mb-3">
                      <p className="text-xs text-cool-gray uppercase tracking-wide mb-1">They were...</p>
                      <p className="text-elegant-black italic">
                        {contrib.content.personality_traits}
                      </p>
                    </div>
                  )}

                  {contrib.content.additional_notes && (
                    <div>
                      <p className="text-xs text-cool-gray uppercase tracking-wide mb-1">Additional</p>
                      <p className="text-elegant-black leading-relaxed">
                        {contrib.content.additional_notes}
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">

            {/* Collaborators panel */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-serif text-base text-space-cadet font-semibold mb-3">
                Invited Collaborators
              </h3>
              {collaborators.length === 0 ? (
                <p className="text-cool-gray text-sm">No collaborators invited yet.</p>
              ) : (
                <ul className="space-y-3">
                  {collaborators.map((collab) => (
                    <li key={collab.id} className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-space-cadet">
                          {collab.full_name || collab.email}
                        </p>
                        {collab.full_name && (
                          <p className="text-xs text-cool-gray">{collab.email}</p>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        collab.status === 'contributed'
                          ? 'bg-green-50 text-green-700'
                          : collab.status === 'active'
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {collab.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Invite more link */}
              <button
                onClick={() => {
                  const params = new URLSearchParams({
                    eulogy_id: sessionId || '',
                    name: deceasedName,
                    type: sessionType,
                  });
                  // Pass inviter name so the invite email reads with the actual
                  // sender, not "Someone" — backend Clerk path doesn't get hit
                  // because the static page can't pass a Bearer token.
                  const inviterDisplayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
                  if (inviterDisplayName) {
                    params.set('userName', inviterDisplayName);
                  }
                  window.location.href = `/invite-collaborators.html?${params.toString()}`;
                }}
                className="mt-4 w-full text-center text-sm text-space-cadet-primary hover:underline"
              >
                + Invite more collaborators
              </button>
            </div>

            {/* Presence */}
            {presenceUsers.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-5">
                <h3 className="font-serif text-base text-space-cadet font-semibold mb-3">
                  Currently Viewing
                </h3>
                <ul className="space-y-2">
                  {presenceUsers.map((name, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      <span className="text-space-cadet">{name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Activity feed */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-serif text-base text-space-cadet font-semibold mb-3">
                Recent Activity
              </h3>
              {activity.length === 0 ? (
                <p className="text-cool-gray text-sm">No activity yet.</p>
              ) : (
                <ul className="space-y-3">
                  {activity.slice(0, 10).map((item) => (
                    <li key={item.id} className="text-sm border-l-2 border-gray-100 pl-3">
                      <p className="text-space-cadet">{item.action_description}</p>
                      <p className="text-xs text-cool-gray">{timeAgo(item.created_at)}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CollaborationDashboard;
